import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {message} from "antd";
import {createLiveEventRepository} from "../data/repositories";
import {AnalyticsEvent} from "../../../utils/analytics";
import type {
  LiveEvent,
  LiveEventFilter,
  PaginatedLiveEvents,
  CreateLiveEventRequest,
  UpdateLiveEventRequest,
  PaginationParams,
  JoinLiveEventResult,
  RescheduleLiveEventRequest,
} from "../domain/types";

const QUERY_KEY = "live-events";

type ApiError = AxiosError<{status: boolean; message: string}>;

/**
 * Hook for fetching a single live event by ID.
 *
 * @example
 * const { data: event, isLoading } = useLiveEvent(42);
 */
export const useLiveEvent = (id: number) => {
  const repository = createLiveEventRepository();

  return useQuery<LiveEvent, ApiError>({
    queryKey: [QUERY_KEY, id],
    queryFn: () => repository.getById(id),
    enabled: !!id,
  });
};

interface UseLiveEventsOptions extends PaginationParams {
  filter: LiveEventFilter;
}

/**
 * Hook for fetching paginated list of live events.
 *
 * @example
 * const { data, isLoading } = useLiveEvents({
 *   filter: "upcoming",
 *   page: 1,
 *   perPage: 15
 * });
 * // data.data = LiveEvent[]
 * // data.total = number
 * // data.hasNextPage = boolean
 */
export const useLiveEvents = (options: UseLiveEventsOptions) => {
  const repository = createLiveEventRepository();

  return useQuery<PaginatedLiveEvents, ApiError>({
    queryKey: [QUERY_KEY, "list", options],
    queryFn: () =>
      repository.getList(options.filter, {
        page: options.page,
        perPage: options.perPage,
      }),
    keepPreviousData: true,
  });
};

/**
 * Hook for creating a new live event.
 *
 * @example
 * const { mutate, isLoading } = useCreateLiveEvent();
 * mutate({ name: "My Event", ... });
 */
export const useCreateLiveEvent = () => {
  const repository = createLiveEventRepository();
  const queryClient = useQueryClient();

  return useMutation<LiveEvent, ApiError, CreateLiveEventRequest>({
    mutationFn: (request) => repository.create(request),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
      AnalyticsEvent("Live events", "Live class create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Live events", "Live class create success");
      queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
    },
  });
};

/**
 * Hook for updating an existing live event.
 *
 * @example
 * const { mutate, isLoading } = useUpdateLiveEvent();
 * mutate({ id: 42, name: "Updated Name" });
 */
export const useUpdateLiveEvent = () => {
  const repository = createLiveEventRepository();
  const queryClient = useQueryClient();

  return useMutation<LiveEvent, ApiError, UpdateLiveEventRequest>({
    mutationFn: (request) => repository.update(request),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
      AnalyticsEvent("Live events", "Live class update fail");
    },
    onSuccess: (data) => {
      AnalyticsEvent("Live events", "Live class update success");
      queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
      queryClient.setQueryData([QUERY_KEY, data.id], data);
    },
  });
};

/**
 * Hook for deleting a live event.
 *
 * @example
 * const { mutate, isLoading } = useDeleteLiveEvent();
 * mutate(42);
 */
export const useDeleteLiveEvent = () => {
  const repository = createLiveEventRepository();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, number>({
    mutationFn: (id) => repository.delete(id),
    onError: (error) => {
      message.error(error.response?.data?.message || error.message);
      AnalyticsEvent("Live event", "Live event delete fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Live event", "Live event delete success");
      queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
    },
  });
};

/**
 * Hook for joining a live event as a coach.
 * Returns meeting credentials (token, room, URL) for starting the session.
 *
 * @example
 * const { mutate, isLoading } = useJoinLiveEvent();
 * mutate(eventId, {
 *   onSuccess: (result) => {
 *     window.open(launchMeeting({ joinResult: result, ... }).url, "_blank");
 *   }
 * });
 */
export const useJoinLiveEvent = () => {
  const repository = createLiveEventRepository();

  return useMutation<JoinLiveEventResult, ApiError, number>({
    mutationFn: (eventId) => repository.join(eventId),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Live event", "Live class booking join fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Live event", "Live class booking join success");
    },
  });
};

/**
 * Hook for rescheduling a live event session.
 * Invalidates list and single-event queries on success.
 *
 * @example
 * const { mutate, isLoading } = useRescheduleLiveEvent();
 * mutate({
 *   eventId: 123,
 *   reason: "Conflict with another meeting",
 *   sessionInfo: [...updatedSessions],
 *   previousSessionInfo: [...originalSessions],
 * });
 */
export const useRescheduleLiveEvent = () => {
  const repository = createLiveEventRepository();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, RescheduleLiveEventRequest>({
    mutationFn: (request) => repository.reschedule(request),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Live event", "Coach schedule class fail");
    },
    onSuccess: (_, variables) => {
      AnalyticsEvent("Live event", "Coach schedule class success");
      queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
      queryClient.invalidateQueries({queryKey: [QUERY_KEY, variables.eventId]});
    },
  });
};
